import { useState, type FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@xing-com/button';
import {
  ArticleCard,
  EmptyStateSwitch,
  LinkShareModalPage,
  FS_NEW_LINK_SHARE,
  ArticlesModuleContextProvider,
} from '@xing-com/crate-entity-pages-articles';
import {
  EP_NwtEditViewAction,
  ModuleHeader,
  useEntityPageNwtEditViewAction,
} from '@xing-com/crate-entity-pages-common';
import { useEditStore } from '@xing-com/crate-entity-pages-entity-pages-shared-context-edit-context';
import { usePageContext } from '@xing-com/crate-entity-pages-entity-pages-shared-context-entity-page-context';
import { SocialCountersProvider } from '@xing-com/crate-social-utils';
import { EmptyState, ErrorState } from '@xing-com/empty-state';
import { useFeatureSwitch } from '@xing-com/hub';
import { IconLink, IconPlus } from '@xing-com/icons';
import {
  IllustrationSpotError,
  IllustrationSpotTop,
} from '@xing-com/illustrations';
import { Headline } from '@xing-com/typography';

import { ArticleListSkeleton } from './articles-module.skeleton';
import * as Styled from './articles-module.styles';

export const ArticlesModule: FC = () => {
  const { $t } = useIntl();
  const { pageContext } = usePageContext();
  const isEditing = useEditStore(({ isEditing }) => isEditing);
  const { trackEntityPageEditView } = useEntityPageNwtEditViewAction();

  const [showModal, setShowModal] = useState(false);

  const showNewLinkShare = useFeatureSwitch(FS_NEW_LINK_SHARE, false);

  return (
    <ArticlesModuleContextProvider>
      {({
        error,
        articleEditorPath,
        articleSharePath,
        isEmptyState,
        initialLoading,
        refetchLoading,
        isFeatureArticleVisible,
        articlesUrns,
        articlesCards,
        isShowMoreAvailable,
        fetchMoreLoading,
        refetch,
        showMoreArticles,
      }) => (
        <>
          {isEditing ? (
            <Styled.MobileEditWarningContainer>
              <Headline size="xxxlarge">
                <FormattedMessage id="EP_ARTICLES_MODULE_HEADLINE" />
              </Headline>
              <EmptyState
                illustration={IllustrationSpotError}
                headlineText={$t({
                  id: 'EP-ARTICLES-MODULE-MOBILE-HINT-TITLE',
                })}
                bodyCopyText={$t({
                  id: 'EP-ARTICLES-MODULE-MOBILE-HINT-TEXT',
                })}
                variant="engage"
                size="medium"
                withBackground
              />
            </Styled.MobileEditWarningContainer>
          ) : null}

          <Styled.ArticlesModuleContainer $isEditing={isEditing}>
            <ModuleHeader
              headlineCopyKey="EP_ARTICLES_MODULE_HEADLINE"
              CustomEditButton={[
                <Styled.HeaderEditButton
                  key="EP-ARTICLES-MODULE-WRITE-NEW-ARTICLE-BUTTON"
                  data-testid="EP-ARTICLES-MODULE-WRITE-NEW-ARTICLE-BUTTON"
                  to={articleEditorPath}
                  icon={IconPlus}
                  onClick={() => {
                    trackEntityPageEditView(
                      EP_NwtEditViewAction.ARTICLES_MODULE_EDITOR_OPEN
                    );
                  }}
                >
                  <FormattedMessage id="EP-ARTICLES-MODULE-WRITE-NEW-ARTICLE-BUTTON" />
                </Styled.HeaderEditButton>,
                <Styled.HeaderEditButton
                  key="EP-ARTICLES-MODULE-SHARE-NEW-ARTICLE-BUTTON"
                  data-testid="EP-ARTICLES-MODULE-SHARE-NEW-ARTICLE-BUTTON"
                  icon={IconLink}
                  onClick={
                    showNewLinkShare ? () => setShowModal(true) : undefined
                  }
                  to={showNewLinkShare ? undefined : articleSharePath}
                >
                  <FormattedMessage id="EP-ARTICLES-MODULE-SHARE-NEW-ARTICLE-BUTTON" />
                </Styled.HeaderEditButton>,
              ]}
            />
            {showNewLinkShare ? (
              <LinkShareModalPage
                showModal={showModal}
                setShowModal={setShowModal}
                contextGlobalId={pageContext.contentPageGlobalId}
              />
            ) : null}
            {isEditing ? <Styled.FilterBar /> : null}
            {error ? (
              <ErrorState
                headlineText={$t({ id: 'EP_GENERAL_ERROR_TITLE' })}
                bodyCopyText={$t({ id: 'EP_GENERAL_ERROR_TEXT' })}
                buttonText={$t({ id: 'EP_GENERAL_ERROR_BUTTON' })}
                size="medium"
                withBackground
                buttonProps={{
                  loading: refetchLoading,
                  onClick: refetch,
                }}
              />
            ) : initialLoading || refetchLoading ? (
              <ArticleListSkeleton />
            ) : isEmptyState ? (
              <EmptyStateSwitch path={articleEditorPath} />
            ) : (
              <>
                {isFeatureArticleVisible ? (
                  <Styled.EmptyStateFeatured
                    illustration={IllustrationSpotTop}
                    headlineText={$t({
                      id: 'EP-ARTICLES-MODULE-EMPTY-STATE-PINNED-TITLE',
                    })}
                    bodyCopyText={$t({
                      id: 'EP-ARTICLES-MODULE-EMPTY-STATE-PINNED-TEXT',
                    })}
                    size="medium"
                    variant="engage"
                    withBackground
                  />
                ) : null}
                <Styled.ArticlesGrid data-testid="EP-ARTICLES-MODULE-ARTICLES-GRID">
                  <SocialCountersProvider urns={articlesUrns}>
                    {articlesCards.map((article, position) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        position={position}
                      />
                    ))}
                  </SocialCountersProvider>
                </Styled.ArticlesGrid>
              </>
            )}
            {isShowMoreAvailable ? (
              <Styled.ArticlesModuleFooter>
                <Button
                  data-testid="EP-ARTICLES-MODULE-SHOW-MORE-ARTICLES-BUTTON"
                  variant="secondary"
                  loading={fetchMoreLoading}
                  onClick={showMoreArticles}
                >
                  <FormattedMessage id="EP-ARTICLES-MODULE-SHOW-MORE-ARTICLES-BUTTON" />
                </Button>
              </Styled.ArticlesModuleFooter>
            ) : null}
          </Styled.ArticlesModuleContainer>
        </>
      )}
    </ArticlesModuleContextProvider>
  );
};
